import { createContext } from "react";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";

export interface GraphemePreviewSheetContextValue {
  /**
   * Opens the preview sheet on `grapheme`, or — when the sheet is already open — pushes it onto
   * the sheet's own back stack so an in-sheet link swaps the previewed character in place instead
   * of stacking a second sheet.
   */
  openGraphemePreview: (grapheme: string) => void;
}

/**
 * Bracketed mnemonic links open a preview of the linked grapheme instead of navigating the app to
 * it, so a reader can check what a component means without losing the character they were
 * studying. Decomposition components are deliberately not routed here — a tap there navigates to
 * the component's own page.
 *
 * The default is a no-op rather than a thrown error: the mnemonic meaning panel renders on
 * surfaces that mount no preview sheet (the Reference page, review cards), and there a link tap
 * must fall back to real navigation — see `useGraphemeLinkPress`.
 */
export const GraphemePreviewSheetContext = createContext<GraphemePreviewSheetContextValue | null>(
  null,
);

/**
 * The href prefix a previewed grapheme's panel renders against. The preview has no route of its
 * own, so it borrows the host route's prefix — every in-panel navigation the preview still
 * performs (the "Open <glyph>" button) resolves against the page the reader actually came from.
 */
export interface GraphemePreviewSheetHostHref {
  morphemeHrefObject: MorphemeHrefObject;
}
